import { CommonModule, TitleCasePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { StickyObserverDirective } from '@directives/sticky-observer/sticky-observer.directive';
import { FieldGroupMetaData, FieldMetaData, FieldsData } from '@models/checkout.model';
import { LineAsset, UserAsset } from '@models/dashboard.model';
import { calculateBulkOrderItems } from '@models/functions.model';
import { FeaturesRecurringCost } from '@models/manage-features.model';
import { Notification, NotificationProp } from '@models/notifications.model';
import { CartInfo, CostInfo } from '@models/session-details';
import {
  AdminOverrideOptions,
  ExistingAssetDetails,
  OrderDetailsAdditionalInfo,
  OrderDetailsSummary,
  OrderPriceSummaryItem,
  ShoppingCartData,
  StoreOrderInfo
} from '@models/shopping-cart.model';
import { ReviewFields, SelectedOrderApprover, ValidateFields } from '@models/shopping.model';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { CustomFormService } from '@services/custom-form/custom-form.service';
import { CustomizeService } from '@services/customize-service/customize.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { CustomFormComponent } from '@shared-components/custom-form/custom-form.component';
import { Subscription, startWith } from 'rxjs';
import {
  DataLoaderCaptureOverrideDirective,
  DataLoaderDirective,
  NotificationsComponent,
  ViewService
} from 'tangoe-design-system';

import { AdminOverrideBannerComponent } from '../admin-override/admin-override-banner/admin-override-banner.component';
import { OrderApproversComponent } from '../order-approvers/order-approvers.component';
import { OrderDetailsComponent } from '../order-details/order-details.component';

@Component({
  selector: 'ts-review',
  templateUrl: './review.component.html',
  styleUrls: [],
  hostDirectives: [DataLoaderCaptureOverrideDirective],
  imports: [
    CommonModule,
    NgbTooltipModule,
    TranslationPipe,
    DataLoaderDirective,
    StickyObserverDirective,
    AdminOverrideBannerComponent,
    CustomFormComponent,
    NotificationsComponent,
    OrderApproversComponent,
    OrderDetailsComponent
  ],
  providers: [TitleCasePipe]
})
export class ReviewComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Output() valid_approver_form: EventEmitter<boolean> = new EventEmitter();
  store_order_id: number;
  form: FormGroup;
  order_type_id: number;
  order_id: number;
  is_submitting_overrides: boolean = false;
  is_review_called: boolean = false;
  valid_approvers: boolean = false;
  skip_all_overrides: boolean = false;
  skip_approval: boolean = false;
  skip_policy_agreement: boolean = false;
  suppress_user_email: boolean = false;
  toggle_controls_disabled: boolean = false;
  selected_approvers: SelectedOrderApprover[] = [];
  notifications: Map<string, NotificationProp> = new Map<string, NotificationProp>();
  approval_notification: NotificationProp | undefined;
  json_form_data: FieldsData['groups'];
  source_order_details: OrderDetailsSummary['source_order'];
  price_summary: OrderDetailsSummary['order_price_summary'];
  existing_asset: UserAsset;
  additional_info: OrderDetailsAdditionalInfo;
  keep_plan: ShoppingCartData['keep_plan'];
  shopping_cart: ShoppingCartData;
  admin_options: AdminOverrideOptions;
  private subscriptions = new Subscription();

  constructor(
    private activated_route: ActivatedRoute,
    private activity_manager_svc: ActivityManagerService,
    private cdr: ChangeDetectorRef,
    private custom_form_svc: CustomFormService,
    private customize_svc: CustomizeService,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService,
    private view_svc: ViewService
  ) {}

  ngOnInit(): void {
    this.getCartItems();

    this.subscriptions.add(
      this.activated_route.queryParamMap.subscribe(() => {
        this.store_order_id = this.activated_route.snapshot.params['id'];
        if (this.store_order_id) {
          this.shopping_svc.getOrderDetails(this.store_order_id).subscribe({
            next: (res: OrderDetailsSummary) => {
              this.order_id = res.id;
              this.source_order_details = res.source_order;
              this.price_summary = res.order_price_summary;
              this.order_type_id = res.store_order_type_id;
              if (res.existing_asset)
                this.existing_asset = { ...res.existing_asset, is_review: true };
              this.additional_info = res.store_order_additional_info!;
              this.keep_plan = res?.keep_plan;

              if (this.price_summary?.bulk_order_additional_info?.bulk_quantity)
                this.price_summary = calculateBulkOrderItems(this.price_summary);
            }
          });
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  get disable_order_submit(): boolean {
    return this.form && this.form.valid && this.valid_approvers ? false : true;
  }

  get is_admin(): boolean {
    return this.session_svc.getUserProfile()?.admin ?? false;
  }

  get is_total_recurring_cost(): boolean {
    if ([11, 12].includes(this.order_type_id)) return true;
    return false;
  }

  get bulk_accessories_length(): boolean {
    return this.price_summary?.bulk_order_additional_info?.bulk_accessories &&
      this.price_summary.bulk_order_additional_info.bulk_accessories.length > 0
      ? true
      : false;
  }

  get bulk_features_length(): boolean {
    return this.price_summary?.bulk_order_additional_info?.bulk_features &&
      this.price_summary.bulk_order_additional_info.bulk_features.length > 0
      ? true
      : false;
  }

  get footer_hint(): string | undefined {
    const date: Date = new Date();
    const line: LineAsset | undefined = this.existing_asset?.line;
    const contract_end_date: Date = new Date(line?.contract_end_date!);

    if (
      this.isEmployeePayable() &&
      this.price_summary?.total_features_recurring_cost?.price
        .difference_cost_to_currency_in_cents != 0
    ) {
      return this.translation_svc.translate('payment_method_disclosure');
    } else if (line && line.contract_end_date && date.getTime() <= contract_end_date.getTime()) {
      const val: string = new Date(line.contract_end_date).toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      return this.translation_svc.translate('etf_warning', [line.carrier_name!, val]);
    } else if (this.order_type_id === 12) {
      if (
        this.price_summary?.total_features_recurring_cost?.price
          .difference_cost_to_currency_in_cents === 0
      ) {
        return this.translation_svc.translate('monthly_bill_no_change');
      }
      const price: FeaturesRecurringCost['price'] =
        this.price_summary?.total_features_recurring_cost?.price!;
      return this.translation_svc.translate('monthly_bill_will', [
        price.monthly_bill,
        price.difference_cost_to_currency
      ]);
    }

    return;
  }

  get hide_price_content(): boolean {
    if ([6, 7, 8, 9, 10, 13, 15, 16, 19].includes(this.order_type_id)) return false;
    return true;
  }

  get cancel_order_hint(): string {
    return [1, 2, 3, 5].includes(this.order_type_id)
      ? this.translation_svc.translate('clear_cart_warning')
      : this.translation_svc.translate('clear_order_warning');
  }

  get asset_details(): ExistingAssetDetails {
    return {
      existing_asset: this.existing_asset,
      keep_plan: this.keep_plan,
      existing_person: this.source_order_details.existing_person,
      replacement_user: this.source_order_details.replacement_user
    };
  }

  getFeatureType = (item: OrderPriceSummaryItem): string => this.customize_svc.featureType(item);

  isEmployeePayable(type?: string): boolean | undefined {
    if (type) {
      if (type === 'addon')
        return Boolean(
          this.price_summary?.one_time_costs?.find(
            (cost: OrderPriceSummaryItem) =>
              cost.employee_cost_to_currency && cost.type === 'ACCESSORY'
          )
        );
      else if (type === 'device')
        return Boolean(
          this.price_summary?.one_time_costs?.find(
            (cost: OrderPriceSummaryItem) =>
              cost.employee_cost_to_currency && cost.type === 'DEVICE'
          )
        );
    }

    return Boolean(
      this.price_summary?.one_time_costs?.find(
        (cost: OrderPriceSummaryItem) =>
          cost.employee_cost_to_currency && ['ACCESSORY', 'DEVICE'].includes(cost.type)
      )
    );
  }

  approvalNotification(event: Notification): void {
    this.notifications.set('approval_notification', {
      type: 'approval_notification',
      notification_list: [event],
      icon: 'notifications',
      color: 'info'
    });
    this.approval_notification = this.notifications.get('approval_notification');
  }

  toggleAllOverrides(toggle_all: boolean): void {
    this.skip_all_overrides = toggle_all;

    if (toggle_all) {
      this.skip_approval = this.skip_policy_agreement = this.suppress_user_email = true;
      this.toggle_controls_disabled = true;

      if (this.notifications.get('approval_notification'))
        this.notifications.delete('approval_notification');
    } else {
      this.skip_approval = this.skip_policy_agreement = this.suppress_user_email = false;
      this.toggle_controls_disabled = false;

      if (this.approval_notification)
        this.notifications.set('approval_notification', this.approval_notification);
    }

    this.persistAdminOptionsAndRefresh();
  }

  toggleSkipApprovals(skip_approval: boolean): void {
    if (this.skip_approval === skip_approval) return this.persistAdminOptionsAndRefresh();

    this.skip_approval = skip_approval;

    this.persistAdminOptionsAndRefresh();

    if (skip_approval && this.notifications.get('approval_notification'))
      this.notifications.delete('approval_notification');
    else if (!skip_approval && this.approval_notification)
      this.notifications.set('approval_notification', this.approval_notification);
  }

  toggleSkipPolicyAgreement(skip_policy_agreement: boolean): void {
    if (this.skip_policy_agreement === skip_policy_agreement)
      return this.persistAdminOptionsAndRefresh();

    this.skip_policy_agreement = skip_policy_agreement;

    this.persistAdminOptionsAndRefresh();
  }

  toggleSupressUserEmail(suppress_user_email: boolean): void {
    if (this.suppress_user_email === suppress_user_email)
      return this.persistAdminOptionsAndRefresh();

    this.suppress_user_email = suppress_user_email;

    this.persistAdminOptionsAndRefresh();
  }

  validateReviewFields(): void {
    const payload: StoreOrderInfo = this.generateParam(
      undefined,
      this.skip_all_overrides,
      this.skip_approval,
      this.skip_policy_agreement,
      this.suppress_user_email
    );

    this.shopping_svc.validateReviewFields(this.store_order_id, payload).subscribe({
      next: (res: ValidateFields) => {
        if (res.valid) this.updatePlaceOrder();
        else {
          this.view_svc.resetScrollPosition();
          this.notifications.set('validate_notification', {
            type: 'validate_notification',
            notification_list: this.custom_form_svc.validationNotificationsBanner(res),
            icon: 'close',
            color: 'danger'
          });
          this.json_form_data = this.custom_form_svc.setFormErrors(this.json_form_data, res);
        }
      },
      error: (err: HttpErrorResponse) => {}
    });
  }

  cancelOrder(): void {
    this.activity_manager_svc.cancelCurrentActivity(true);
  }

  approverFormIsValid(valid: boolean): void {
    this.valid_approvers = valid;
  }

  setSelectedApprovers(approvers: SelectedOrderApprover[]): void {
    this.selected_approvers = approvers;
  }

  private updateField(param: StoreOrderInfo, data_loader_key?: string): void {
    this.shopping_svc
      .getReviewFields(this.store_order_id, param, data_loader_key)
      .subscribe((res: ReviewFields) => {
        this.notifications.delete('validate_notification');
        this.json_form_data = res.groups;
        this.activity_manager_svc.state().setSteps(res.progress_bar_config.steps);
        this.form = this.custom_form_svc.createForm(this.json_form_data);
        this.json_form_data.forEach((group: FieldGroupMetaData) => {
          group.fields.forEach((field: FieldMetaData) => {
            if (field.on_change) {
              field.control.valueChanges.subscribe(() => {
                if (field.type === 'select' && field.control.value === '') return;

                this.updateField(
                  this.generateParam(
                    field.name,
                    this.skip_all_overrides,
                    this.skip_approval,
                    this.skip_policy_agreement,
                    this.suppress_user_email
                  ),
                  `order-fields-${group.name}`
                );
              });
            }
          });
        });

        this.form.valueChanges
          .pipe(startWith(this.updateFieldsValue()))
          .subscribe(() => this.updateFieldsValue());
      });
  }

  private updateFieldsValue(): void {
    this.generateParam(
      undefined,
      this.skip_all_overrides,
      this.skip_approval,
      this.skip_policy_agreement,
      this.suppress_user_email
    ).fields = this.custom_form_svc.parseFieldValue(this.json_form_data, this.source_order_details);
  }

  private generateParam(
    field_name?: string,
    select_all_overrides?: boolean | undefined,
    skip_approval?: boolean | undefined,
    skip_policy_agreement?: boolean | undefined,
    suppress_user_email?: boolean | undefined
  ): StoreOrderInfo {
    return {
      fields: this.json_form_data?.length
        ? this.custom_form_svc.parseFieldValue(this.json_form_data, this.source_order_details)
        : [],
      on_change_field: field_name,
      approver_info: {
        approvers: this.selected_approvers
      },
      skip_policy_agreement: skip_policy_agreement,
      select_all_overrides: select_all_overrides,
      skip_approval: skip_approval,
      suppress_user_email: suppress_user_email,
      add_additional_line: this.shopping_cart?.add_additional_line ?? false
    };
  }

  private updatePlaceOrder(): void {
    const payload: StoreOrderInfo = this.generateParam(
      undefined,
      this.skip_all_overrides,
      this.skip_approval,
      this.skip_policy_agreement,
      this.suppress_user_email
    );
    this.shopping_svc.updatePlacedOrder(this.store_order_id, payload).subscribe({
      next: (res: OrderDetailsSummary) => {
        this.clearCartItems();
        this.activity_manager_svc.navigateToConfirmation(this.store_order_id);
      },
      error: (err: HttpErrorResponse) => {
        if ((err.error as ValidateFields) && !err.error.valid) {
          this.view_svc.resetScrollPosition();
          this.notifications.set('validate_notification', {
            type: 'validate_notification',
            notification_list: this.custom_form_svc.validationNotificationsBanner(err.error),
            icon: 'close',
            color: 'danger'
          });
          this.json_form_data = this.custom_form_svc.setFormErrors(this.json_form_data, err.error);
        }
      }
    });
  }

  private clearCartItems(): void {
    this.shopping_svc.clearShoppingCartItems().subscribe({
      next: (res: ShoppingCartData) => {
        CostInfo.one_time_cost = '- -';
        CostInfo.recurring_cost = '- -';
        CartInfo.cart_data = undefined;
      },
      error: (err: HttpErrorResponse) => {}
    });
  }

  private getCartItems(): void {
    this.shopping_svc.getShoppingItems().subscribe({
      next: (res: ShoppingCartData) => {
        this.shopping_cart = res;
        const selectAll: boolean = !!this.shopping_cart.admin_options?.select_all;
        this.skip_all_overrides = selectAll;
        if (selectAll) {
          this.skip_approval = this.skip_policy_agreement = this.suppress_user_email = true;
          this.toggle_controls_disabled = true;
        } else {
          this.skip_approval = !!this.shopping_cart.admin_options?.skip_approval;
          this.skip_policy_agreement = !!this.shopping_cart.admin_options?.skip_policy_agreement;
          this.suppress_user_email = !!this.shopping_cart.admin_options?.suppress_user_email;
          this.toggle_controls_disabled = false;
        }

        if (this.store_order_id && !this.is_review_called) {
          const payload: StoreOrderInfo = this.generateParam(
            undefined,
            this.skip_all_overrides,
            this.skip_approval,
            this.skip_policy_agreement,
            this.suppress_user_email
          );
          this.updateField(payload);
          this.is_review_called = true;
        }
      },
      error: (err: HttpErrorResponse) => {}
    });
  }

  private persistAdminOptionsAndRefresh(): void {
    const payload: StoreOrderInfo = this.generateParam(
      undefined,
      this.skip_all_overrides,
      this.skip_approval,
      this.skip_policy_agreement,
      this.suppress_user_email
    );

    this.shopping_cart.admin_options = {
      select_all: this.skip_all_overrides,
      skip_approval: this.skip_approval,
      skip_policy_agreement: this.skip_policy_agreement,
      suppress_user_email: this.suppress_user_email
    };

    if (this.is_submitting_overrides) return;

    this.is_submitting_overrides = true;
    this.shopping_svc.sendShoppingItems(this.shopping_cart).subscribe({
      next: (res: ShoppingCartData) => {
        this.shopping_cart = res;

        const selectAll: boolean = !!this.shopping_cart.admin_options?.select_all;
        this.skip_all_overrides = selectAll;

        if (selectAll) {
          this.skip_approval = this.skip_policy_agreement = this.suppress_user_email = true;
          this.toggle_controls_disabled = true;

          if (this.notifications.get('approval_notification'))
            this.notifications.delete('approval_notification');
        } else {
          this.skip_approval = !!this.shopping_cart.admin_options?.skip_approval;
          this.skip_policy_agreement = !!this.shopping_cart.admin_options?.skip_policy_agreement;
          this.suppress_user_email = !!this.shopping_cart.admin_options?.suppress_user_email;
          this.toggle_controls_disabled = false;

          if (!this.skip_approval && this.approval_notification)
            this.notifications.set('approval_notification', this.approval_notification);
        }

        this.updateField(payload);
        this.is_submitting_overrides = false;
      },
      error: (err: HttpErrorResponse) => {}
    });
  }
}
